'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, InputNumber, notification } from 'antd';
import type { MenuProps } from 'antd';
import type { RatioDropdown } from '@/types/commontypes';
import { globalShapeStore } from '@/stores/globalShapeStore';

/** TP/SL 드롭다운에 표시할 비율 프리셋 목록 */
export const TPSL_PRESETS = [1, 1.5, 2, 2.5];

/**
 * TP/SL 비율 드롭다운 메뉴 구성 — RatioForm과 PlaceOrderPopup에서 공유.
 * 열고 닫는 방식은 사용처에서 각자 처리한다.
 *
 * inputValue / onInputChange: 사용처 state를 그대로 연결 (클로저 문제 방지)
 */
export function buildTpslMenu(options: {
  currentRatio: number;
  color: string;
  inputValue: number | null;
  onInputChange: (v: number | null) => void;
  onSelect: (ratio: number) => void;
  onClose: () => void;
  width?: number;
}): MenuProps {
  const { currentRatio, color, inputValue, onInputChange, onSelect, onClose, width } = options;

  const handleEnter = () => {
    if (inputValue !== null && inputValue < 1) {
      notification.warning({
        message: 'TP/SL 비율 오류',
        description: '비율은 1 이상이어야 합니다.',
        placement: 'topRight',
        duration: 3,
      });
      return;
    }
    if (inputValue !== null && inputValue >= 1) {
      onSelect(inputValue);
      onInputChange(null);
    }
    onClose();
  };

  const items: MenuProps['items'] = [
    {
      key: 'custom',
      label: (
        <InputNumber
          value={inputValue}
          size="small"
          min={0.01}
          max={100}
          step={0.1}
          controls={false}
          placeholder="직접 입력"
          style={{ width: '100%' }}
          onChange={v => onInputChange(v)}
          onClick={e => e.stopPropagation()}
          onPressEnter={handleEnter}
        />
      ),
    },
    {
      key: 'enter',
      label: (
        <button
          type="button"
          style={{
            width: '100%',
            backgroundColor: color,
            color: 'white',
            border: 'none',
            borderRadius: 4,
            padding: '2px 0',
            fontWeight: 'normal',
            cursor: 'pointer',
            fontSize: 12,
          }}
          onClick={e => {
            e.stopPropagation();
            handleEnter();
          }}
        >
          Enter
        </button>
      ),
    },
    { type: 'divider' },
    ...TPSL_PRESETS.map(p => ({
      key: String(p),
      label: <span style={{ float: 'right' }}>{String(parseFloat(p.toFixed(2)))}</span>,
    })),
  ];

  return {
    items,
    selectedKeys: TPSL_PRESETS.filter(p => p.toFixed(2) === currentRatio.toFixed(2)).map(p => String(p)),
    style: { width: width ?? 130 },
    onClick: ({ key }) => {
      if (key === 'custom' || key === 'enter') return;
      onSelect(Number(key));
    },
  };
}

interface RatioFormProps {
  dropdown: RatioDropdown;
  setRatioDropdown?: (dropdown: Partial<RatioDropdown>) => void;
  /** ratio 선택 시 호출. 전역 상태를 거치지 않고 해당 노드에 직접 전달됨 */
  onSelect?: (uuid: string, ratio: number) => void;
}

export const RatioForm: React.FC<RatioFormProps> = ({ dropdown, setRatioDropdown, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState<number | null>(null);
  const isMenuHoveredRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // dropdown.hovering → 열기. false → 메뉴 안에 없으면 80ms 후 닫기
  useEffect(() => {
    if (dropdown.hovering) {
      setOpen(true);
      return undefined;
    }
    const timer = setTimeout(() => {
      if (!isMenuHoveredRef.current) setOpen(false);
    }, 80);
    return () => clearTimeout(timer);
  }, [dropdown.hovering]);

  // 드롭다운 외부 클릭 시 강제 닫기
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as EventTarget;

      if (containerRef.current && !containerRef.current.contains(target as unknown as Node)) {
        const popup = document.querySelector('.ant-dropdown:not(.ant-dropdown-hidden)');

        if (popup && popup.contains(target as unknown as Node)) return;
        setOpen(false);
        setRatioDropdown?.({ hovering: false });
      }
    };
    window.addEventListener('mousedown', handleMouseDown);
    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('mousedown', handleMouseDown);
  }, [open, setRatioDropdown]);

  const currentRatio = dropdown.positionType
    ? globalShapeStore.getState().lastTPSLByDirection[dropdown.positionType]
    : 0;

  const color = dropdown.color ?? '#F47515';

  const menu = buildTpslMenu({
    currentRatio,
    color,
    inputValue,
    onInputChange: setInputValue,
    onSelect: ratio => {
      onSelect?.(dropdown.uuid, ratio);
      if (dropdown.positionType) {
        globalShapeStore.getState().updateLastTPSLByDirection(dropdown.positionType, ratio);
      }
      setOpen(false);
    },
    onClose: () => setOpen(false),
    width: 110,
  });

  const x = dropdown.position?.x ?? 0;
  const y = (dropdown.position?.y ?? 0) + 32;

  return (
    <div ref={containerRef} className="fixed z-1000" style={{ left: x, top: y, width: 0, height: 0 }}>
      <Dropdown
        menu={{
          ...menu,
          onMouseEnter: () => {
            isMenuHoveredRef.current = true;
          },
          onMouseLeave: () => {
            isMenuHoveredRef.current = false;
            if (!dropdown.hovering) setOpen(false);
          },
        }}
        trigger={[]}
        open={open}
        onOpenChange={o => {
          if (!o && !isMenuHoveredRef.current) setOpen(false);
        }}
        placement="bottomLeft"
        getPopupContainer={() => document.body}
      >
        <span />
      </Dropdown>
    </div>
  );
};
